import React, { lazy, Suspense } from 'react';
import styled from 'styled-components';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import Col from 'react-bootstrap/Col';
import ToggleOnOffButton from '../../../../components/Button/ToggleOnOffButton';
import { createWarehouse, updateWarehouse } from '../services/warehouseService';
import { useWarehouse } from '../hooks/useWarehouse';
import { 
  createNewLocationSection,
  validateLocationSections,
  validateWarehouseForm,
  formatLocationsForSubmission,
  updateLocationData,
} from '../utils/locationHelpers';
import SelectAndSearchComponent from "../../../../components/SelectAndSearchComponent/SelectAndSearchComponent";

const GoogleMaps = lazy(() => import('./GoogleMaps'));

const HelpPageWrapper = styled.div`
  background-color: #f7f7f7;
  position: relative;
  padding: 50px 0 50px 0;
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  @media (max-width: 989px) {
    padding-top: 50px;
  }
`;

const WarehouseCreateOrEditForm = ({ id, history, suppliers }) => {
  // Use custom hooks to manage state
  const {
    editMode,
    warehouseName, setWarehouseName,
    warehouseSupplier, setWarehouseSupplier,
    warehouseAddress, setWarehouseAddress,
    warehouseComment, setWarehouseComment,
    IsDSWarehouse, setIsDSWarehouse,
    IsMainWarehouse, setIsMainWarehouse,
    IsDefaultWarehouse, setIsDefaultWarehouse,
    coordinate, setCoordinate,
    formErrors, setFormErrors,
    locationSections, setLocationSections,
    errors, setErrors,
  } = useWarehouse(id);
  
  // Event handlers
  const handleAddLocationSection = () => {
    const { errors, isValid } = validateLocationSections(locationSections);
    
    if (!isValid) {
      setErrors(errors);
      const errorMessages = Object.values(errors)
        .flatMap((sectionErrors) => Object.values(sectionErrors))
        .join("\n");
    
      alert(`Fix the following errors before adding a new location.\n${errorMessages}`);
      return;
    }
    
    const newLocation = createNewLocationSection(locationSections);
    const newSections = [...locationSections, newLocation];
    setLocationSections(newSections);
  };

  const handleRemoveLocationSection = (elementId) => {
    if (locationSections.length <= 1) {
      alert("At least one Location section is required.");
      return;
    }
    
    const newSections = locationSections.filter(section => section.element_id !== elementId);
    setLocationSections(newSections);
    
    if (errors[elementId]) {
      const newErrors = { ...errors };
      delete newErrors[elementId];
      setErrors(newErrors);
    }
  };

  const handleUpdateLocationData = (element_id, field, value) => {
    setLocationSections((prevSections) => 
      updateLocationData(prevSections, element_id, field, value)
    );
  };

  const toggleIsDsWarehouseEnabled = () => {
    setIsDSWarehouse(prev => !prev);
  };

  const toggleIsMainWarehouseEnabled = () => {
    setIsMainWarehouse(prev => !prev);
  };

  const toggleIsDefaultWarehouseEnabled = () => {
    setIsDefaultWarehouse(prev => !prev);
  };

  const onLocationSelect = (latitude, longitude) => {
    setCoordinate({ latitude, longitude });
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      const latLngPosition = new window.google.maps.LatLng(latitude, longitude);
      geocoder.geocode({ location: latLngPosition }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setWarehouseAddress(results[0].formatted_address);
        }
      });
    } else {
      console.error('Google Maps not available yet');
    }
  };

  const handleSupplierChange = (e) => {
    setWarehouseSupplier(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate main form
    const formValidation = validateWarehouseForm({
      warehouseName,
      warehouseSupplier,
    });
    
    if (!formValidation.isValid) {
      setFormErrors(formValidation.errors);
      return;
    }

    if (locationSections.length === 0) {
      alert("At least one Location is required.");
      return;
    }

    // Validate locations
    const { errors, isValid } = validateLocationSections(locationSections);
    
    if (!isValid) {
      setErrors(errors);
      const errorMessages = Object.values(errors)
        .flatMap((sectionErrors) => Object.values(sectionErrors))
        .join("\n");
    
      alert(`Fix the following errors before submitting.\n${errorMessages}`);
      return;
    }

    const contentData = {
      name: warehouseName,
      supplier_id: warehouseSupplier?.store_user_id,
      address: warehouseAddress,
      comment: warehouseComment,
      is_ds_warehouse: IsDSWarehouse,
      is_main_warehouse: IsMainWarehouse,
      is_default: IsDefaultWarehouse,
      lat: coordinate.latitude,
      lng: coordinate.longitude,
      locations: formatLocationsForSubmission(locationSections),
    };

    try {
      let response;
      if (editMode) {
        response = await updateWarehouse(id, contentData);
      } else {
        response = await createWarehouse(contentData);
      }

      if (!response.success) {
        alert('Error: Unable to save warehouse data. Please try again.');
      } else {
        history.push('/admin/warehouses');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error: Something went wrong. Please try again.');
    }
  };

  let contentSupplier = null;
  if (suppliers) {
    const sortedSuppliers = [...suppliers].sort((a, b) => {
      const nameA = a.store_users[0].first_name.toLowerCase();
      const nameB = b.store_users[0].first_name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    contentSupplier = sortedSuppliers.map((supplier, key) => (
      <option key={key} value={supplier.store_user_id}>
        {supplier.store_users[0].first_name}
      </option>
    ));
  }

  return (
    <HelpPageWrapper>
      <Form onSubmit={handleSubmit}>
        <Form.Group as={Col} controlId="formGridName">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Name"
            value={warehouseName}
            onChange={(e) => setWarehouseName(e.target.value)}
            required
            isInvalid={!!formErrors.warehouseName}
          />
          <Form.Control.Feedback type="invalid">{formErrors.warehouseName}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} controlId="formGridSupplier">
          <Form.Label>Supplier</Form.Label>
          {/* <Form.Control
            as="select"
            required
            value={warehouseSupplier}
            onChange={handleSupplierChange}
            isInvalid={!!formErrors.warehouseSupplier}
          >
            <option value={0}>Select Supplier</option>
            {contentSupplier}
          </Form.Control>
          <Form.Control.Feedback type="invalid">{formErrors.warehouseSupplier}</Form.Control.Feedback> */}
          <SelectAndSearchComponent
            options={suppliers}
            value={warehouseSupplier}
            onChange={handleSupplierChange}
            placeholder="Select Supplier"
            getOptionValue={(opt) => opt.store_users[0].first_name}
            className={!!formErrors.warehouseSupplier ? 'is-invalid' : ''}
            getOptionLabel={(supplier) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {supplier.store_users[0].first_name}
                </div>
            )}
        />
        {!!formErrors.warehouseSupplier && <Form.Control.Feedback type="invalid">
            product supplier is required
        </Form.Control.Feedback>}
        </Form.Group>
        <Form.Group as={Col} controlId="formGridAddress">
          <Form.Label>Address</Form.Label>
          <Form.Control
            type="text"
            placeholder="Address"
            value={warehouseAddress}
            onChange={(e) => setWarehouseAddress(e.target.value)}
            isInvalid={!!formErrors.warehouseAddress}
          />
          <Form.Control.Feedback type="invalid">{formErrors.warehouseAddress}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} controlId="formGridComment">
          <Form.Label>Comment</Form.Label>
          <Form.Control
            type="text"
            placeholder="Comment"
            value={warehouseComment}
            onChange={(e) => setWarehouseComment(e.target.value)}
          />
        </Form.Group>
        <Form.Row controlId="formGridMainWar  ehouseButton" style={{paddingRight: '15px', paddingLeft: '15px'}}>
          <Form.Group as={Col} controlId="formGridDSButton">
            <Form.Label>Is DS warehouse?</Form.Label> &nbsp;
            <ToggleOnOffButton checked={IsDSWarehouse} onToggle={toggleIsDsWarehouseEnabled} onLabel={'Yes'} offLabel={'No'} />
          </Form.Group>
          <Form.Group as={Col} controlId="formGridDSButton">
            <Form.Label>Is Main warehouse?</Form.Label> &nbsp;
            <ToggleOnOffButton checked={IsMainWarehouse} onToggle={toggleIsMainWarehouseEnabled} onLabel={'Yes'} offLabel={'No'} />
          </Form.Group>
          <Form.Group as={Col} controlId="formGridDSButton">
            <Form.Label>Is Default?</Form.Label> &nbsp;
            <ToggleOnOffButton checked={IsDefaultWarehouse} onToggle={toggleIsDefaultWarehouseEnabled} onLabel={'Yes'} offLabel={'No'} />
          </Form.Group>
        </Form.Row>
        <hr/>
        <Form.Group as={Col} controlId="formGridLocations">
          <Form.Row style={{ alignContent: "center", alignItems: "center" }}>
            <Form.Group as={Col} md={8} controlId="formGridLocationLabel">
                <Form.Label style={{ marginBottom: 0 }}>Locations</Form.Label>
            </Form.Group>
            <Form.Group as={Col} md={2} controlId="formGridActionColumn">
                <Form.Label>Is Default?</Form.Label> &nbsp;
            </Form.Group>
            <Form.Group as={Col} md={2} controlId="formGridActionColumn">
              <Button type="button" onClick={handleAddLocationSection} variant="primary">
                  Add Location
              </Button>
            </Form.Group>
          </Form.Row>
          {locationSections.map((section) => (
            <Form.Row key={section.element_id}>
              <Form.Group as={Col} md={8} controlId={`formGridLocationName-${section.element_id}`}>
                <Form.Control
                  type="text"
                  placeholder="Location Name"
                  value={section.name || ''}
                  onChange={e => handleUpdateLocationData(section.element_id, 'name', e.target.value)}
                  isInvalid={errors[section.element_id] && !!errors[section.element_id].name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors[section.element_id]?.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md={2} controlId={`formGridDefault-${section.element_id}`} className="d-flex align-items-center">
                  <ToggleOnOffButton 
                    checked={section.is_default} 
                    onToggle={() => handleUpdateLocationData(section.element_id, 'is_default', true)} 
                    onLabel={'Yes'} 
                    offLabel={'No'} 
                  />
              </Form.Group>

              <Form.Group as={Col} md={2} controlId={`formGridAction-${section.element_id}`} className="d-flex align-items-center">
                <Button
                  variant="danger"
                  onClick={() => handleRemoveLocationSection(section.element_id)}
                >
                  <DeleteIcon />
                </Button>
              </Form.Group>
            </Form.Row>
          ))}
        </Form.Group>
        <hr/>
        <Form.Group as={Col} controlId="formGridMap">
          <Suspense fallback={<div>Loading...</div>}>
            <GoogleMaps
              latitude={coordinate.latitude}
              longitude={coordinate.longitude}
              onLocationSelect={onLocationSelect}
            />
          </Suspense>
        </Form.Group>
        <Form.Group as={Col} controlId="formGridButton">
          <Button type="submit">{editMode ? 'Update' : 'Create'}</Button>
        </Form.Group>
      </Form>
    </HelpPageWrapper>
  );
};

export default WarehouseCreateOrEditForm;
